import { computed, inject, Injectable, linkedSignal, resource } from '@angular/core';
import { PaymentRepository } from '../repositories/payment.repository';
import { Payment, PaymentEntry } from '@softapps/wws/pos-core';
import { CartService } from './cart.service';
import { Scan } from '@softline/application';
import { PaymentMethod } from '../types/payment-method';
import { isDefinedNotEmpty } from '@softline/core';

@Injectable()
export class PaymentService {
  readonly #paymentRepository = inject(PaymentRepository);
  readonly #cartService = inject(CartService);

  readonly #cartId = computed(() => {
    return this.#cartService.cart()?.id ?? null;
  });

  readonly #paymentResource = resource({
    params: () => this.#cartId(),
    loader: async (params) => {
      if (!params.params) return null;

      return await this.#paymentRepository.loadPayment(params.params);
    },
  });

  readonly #paymentMethods = resource({
    params: () => this.#cartId() ?? undefined,
    loader: async (params) => {
      if (!params.params) return [];
      return await this.#paymentRepository.loadPaymentMethods(params.params);
    },
  });

  readonly #payment = linkedSignal<Payment | null>(() => {
    return this.#paymentResource.hasValue()
      ? (this.#paymentResource.value() ?? null)
      : null;
  });

  readonly payment = this.#payment.asReadonly();
  readonly paymentMethods = this.#paymentMethods.asReadonly();

  async addPaymentEntry(
    method: PaymentMethod,
    amount: number | undefined,
    cashbackAmount?: number | undefined,
    options?: {
      voucherNumber?: string;
      skipUpdate?: boolean;
    }
  ) {
    const cartId = this.#cartId();
    if (!cartId) return;

    const body = {
      paymentMethod: method,
    };

    if (amount != null && isDefinedNotEmpty(amount)) {
      body['amount'] = amount;
    }

    if (!!cashbackAmount && isDefinedNotEmpty(cashbackAmount)) {
      body['cashbackAmount'] = cashbackAmount;
    }

    if (options && options?.voucherNumber && isDefinedNotEmpty(options?.voucherNumber)) {
      body['voucherNumber'] = options['voucherNumber'];
    }

    const payment = await this.#paymentRepository.addPaymentEntry(cartId, body);
    if (payment) {
      if (options?.skipUpdate !== true) {
        this.#payment.set(payment);
        const cart = this.#cartService.cart();
        if (cart) {
          await this.#cartService.setCart({
            ...cart,
            remainingAmountToPay:
              payment?.remainingAmountToPay ?? cart.remainingAmountToPay,
          });
        }
      }
    }

    return payment;
  }

  set(payment: Payment) {
    this.#payment.set(payment);
  }

  paymentCheck(): Promise<{ possible: boolean; reason?: string }> {
    const cartId = this.#cartId();
    if (!cartId) return Promise.resolve({ possible: false });
    return this.#paymentRepository.paymentCheck(cartId);
  }

  openCashregister(): Promise<void> {
    return this.#paymentRepository.openCashregister();
  }

  reloadPaymentMethods(): void {
    this.#paymentMethods.reload();
  }

  reloadPayment(): void {
    this.#paymentResource.reload();
  }

  blockingReloadPayment(): Promise<Payment | null> {
    const cartId = this.#cartId();
    if (!cartId) return Promise.resolve(null);
    return this.#paymentRepository.loadPayment(cartId);
  }

  async scan(cartId: number, scan: Scan): Promise<Payment | null> {
    const result = await this.#paymentRepository.scan(cartId, scan.labelType, scan.data);
    if (!result) return null;
    this.#paymentResource.set(result);
    return result;
  }

  async printLastTerminalDocument(): Promise<void> {
    return this.#paymentRepository.printLastTerminalDocument();
  }

  async removePaymentEntry(cartId: number, paymentEntryId: PaymentEntry['id']) {
    const payment = await this.#paymentRepository.removePaymentEntry(
      cartId,
      paymentEntryId
    );
    if (payment) {
      this.#payment.set(payment);
    }
  }
}
